<script>
const PROGRAM_TYPES = [
    {
        key: "pregrado",
        title: "Pregrado",
        description: "Carreras para comenzar tu formación profesional.",
        icon: "bx-book-open"
    },
    {
        key: "advance",
        title: "Advance",
        description: "Programas pensados para continuar y potenciar tu trayectoria.",
        icon: "bx-rocket"
    },
    {
        key: "postgrado",
        title: "Postgrado",
        description: "Magísteres, diplomados y cursos de especialización.",
        icon: "bx-award"
    }
];

export default {
    props: {
        logoUrl: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            step: 1,
            programTypes: PROGRAM_TYPES,
            programs: [],
            loadingPrograms: true,
            loadingSchools: false,
            submitting: false,
            showContactFields: false,
            installments: 10,
            complete: false,
            error: "",
            fieldErrors: {},
            search: "",
            regions: [],
            communes: [],
            schools: [],
            form: {
                nombres: "",
                apellido_paterno: "",
                apellido_materno: "",
                rut: "",
                email: "",
                telefono: "",
                grupo: "",
                product_id: null,
                curso: "",
                region_colegio: "",
                comuna_colegio: "",
                colegio: "",
                beneficio_estatal: "",
                beca_indigena: "",
                tiene_cae: "",
                sexo: "",
                pueblo_originario: "",
                discapacidad: "",
                integrantes_hogar: "",
                ingreso_hogar: "",
                nem_promedio: "",
                ranking: "",
                paes_lectora: "",
                paes_m1: "",
                paes_electiva: "",
                paes_electiva_puntaje: "",
                paes_m2: "",
                acepta_contacto: false
            },
            result: null
        };
    },
    computed: {
        selectedProgram() {
            return this.programs.find(program => Number(program.id) === Number(this.form.product_id)) || null;
        },
        filteredPrograms() {
            const term = this.normalize(this.search);

            return this.programs.filter(program => {
                return program.grupo === this.form.grupo
                    && (!term || this.normalize(program.nombre).includes(term));
            });
        },
        currentType() {
            return this.programTypes.find(type => type.key === this.form.grupo) || null;
        },
        offerPrice() {
            if (!this.selectedProgram) return 0;
            return this.form.grupo === "advance"
                ? Math.round(Number(this.selectedProgram.arancel) * 0.65)
                : Number(this.selectedProgram.arancel);
        },
        savings() {
            return this.form.grupo === "advance" && this.selectedProgram
                ? Number(this.selectedProgram.arancel) - this.offerPrice
                : 0;
        },
        fullName() {
            return [this.form.nombres, this.form.apellido_paterno, this.form.apellido_materno]
                .filter(Boolean)
                .join(" ");
        },
        monthlyOffer() {
            return this.result ? Math.round(Number(this.result.offer_price || 0) / 10) : 0;
        },
        installmentAmount() {
            return this.installments > 0 ? Math.round(this.offerPrice / this.installments) : this.offerPrice;
        },
        pregradoSection1Complete() {
            return Boolean(
                String(this.form.nombres || "").trim()
                && String(this.form.apellido_paterno || "").trim()
                && /^[0-9.\-kK]{7,20}$/.test(String(this.form.rut || "").trim())
                && /^\S+@\S+\.\S+$/.test(String(this.form.email || "").trim())
                && String(this.form.telefono || "").trim().length >= 8
                && this.form.sexo
            );
        },
        pregradoSection2Complete() {
            return this.pregradoSection1Complete && Boolean(
                this.form.curso
                && this.form.region_colegio
                && this.form.comuna_colegio
                && this.form.colegio
                && this.form.pueblo_originario
                && this.form.discapacidad
            );
        },
        pregradoSection3Complete() {
            const householdMembers = Number(this.form.integrantes_hogar);
            return this.pregradoSection2Complete && Boolean(
                this.selectedProgram
                && householdMembers >= 1
                && householdMembers <= 20
                && this.form.ingreso_hogar
            );
        },
        pregradoSection4Complete() {
            return this.pregradoSection3Complete && Boolean(
                this.form.beneficio_estatal
                && this.form.beca_indigena
                && this.form.tiene_cae
            );
        },
        pregradoSection5Complete() {
            const scoreIsValid = value => Number(value) >= 100 && Number(value) <= 1000;
            const nem = Number(this.form.nem_promedio);
            const optionalM2IsValid = !this.form.paes_m2 || scoreIsValid(this.form.paes_m2);

            return this.pregradoSection4Complete
                && nem >= 4
                && nem <= 7
                && scoreIsValid(this.form.ranking)
                && scoreIsValid(this.form.paes_m1)
                && scoreIsValid(this.form.paes_lectora)
                && Boolean(this.form.paes_electiva)
                && scoreIsValid(this.form.paes_electiva_puntaje)
                && optionalM2IsValid;
        },
        pregradoCompletedSections() {
            return [
                this.pregradoSection1Complete,
                this.pregradoSection2Complete,
                this.pregradoSection3Complete,
                this.pregradoSection4Complete,
                this.pregradoSection5Complete
            ].filter(Boolean).length;
        }
    },
    mounted() {
        this.loadPrograms();
        this.loadRegions();
    },
    methods: {
        async loadPrograms() {
            try {
                const response = await axios.get("/api/simulador/programas");
                this.programs = response.data.products || [];
            } catch (error) {
                this.error = "No pudimos cargar los programas. Inténtalo nuevamente.";
            } finally {
                this.loadingPrograms = false;
            }
        },
        async loadRegions() {
            try {
                const response = await axios.get("/api/simulador/colegios");
                this.regions = response.data.regions || [];
            } catch (error) {
                this.regions = [];
            }
        },
        async changeRegion() {
            this.form.comuna_colegio = "";
            this.form.colegio = "";
            this.communes = [];
            this.schools = [];
            if (!this.form.region_colegio) return;
            this.loadingSchools = true;
            try {
                const response = await axios.get("/api/simulador/colegios", { params: { region: this.form.region_colegio } });
                this.communes = response.data.communes || [];
            } finally {
                this.loadingSchools = false;
            }
        },
        async changeCommune() {
            this.form.colegio = "";
            this.schools = [];
            if (!this.form.comuna_colegio) return;
            this.loadingSchools = true;
            try {
                const response = await axios.get("/api/simulador/colegios", {
                    params: { region: this.form.region_colegio, comuna: this.form.comuna_colegio }
                });
                this.schools = response.data.schools || [];
            } finally {
                this.loadingSchools = false;
            }
        },
        nextFromData() {
            this.fieldErrors = {};
            const required = ["nombres", "apellido_paterno"];

            required.forEach(field => {
                if (!String(this.form[field] || "").trim()) {
                    this.fieldErrors[field] = ["Este campo es obligatorio."];
                }
            });

            if (Object.keys(this.fieldErrors).length) {
                return;
            }

            this.step = 4;
            this.scrollTop();
        },
        selectType(type) {
            this.form.grupo = type.key;
            this.form.product_id = null;
            this.showContactFields = false;
            this.installments = 10;
            this.search = "";
            this.step = type.key === "pregrado" ? 2 : 3;
            this.scrollTop();
        },
        selectProgram(program) {
            this.form.product_id = program.id;
            this.showContactFields = false;
            this.error = "";
        },
        requestContact() {
            if (!this.selectedProgram) {
                this.error = "Selecciona un programa para continuar.";
                return;
            }

            this.error = "";
            this.showContactFields = true;
            this.$nextTick(() => {
                const emailField = this.$refs.contactEmail;
                if (emailField) emailField.focus();
            });
        },
        continueAcademic() {
            this.fieldErrors = {};
            ["curso", "region_colegio", "comuna_colegio", "colegio"].forEach(field => {
                if (!this.form[field]) this.fieldErrors[field] = ["Este campo es obligatorio."];
            });
            if (Object.keys(this.fieldErrors).length) return;
            this.step = 5;
            this.scrollTop();
        },
        continueBenefits() {
            this.fieldErrors = {};
            ["beneficio_estatal", "beca_indigena", "tiene_cae"].forEach(field => {
                if (!this.form[field]) this.fieldErrors[field] = ["Selecciona una alternativa."];
            });
            if (Object.keys(this.fieldErrors).length) return;
            this.step = 6;
            this.scrollTop();
        },
        continueScores() {
            this.fieldErrors = {};
            const nem = Number(this.form.nem_promedio);
            const ranking = Number(this.form.ranking);
            if (!nem || nem < 4 || nem > 7) this.fieldErrors.nem_promedio = ["Ingresa un promedio entre 4,0 y 7,0."];
            if (!ranking || ranking < 100 || ranking > 1000) this.fieldErrors.ranking = ["Ingresa un puntaje entre 100 y 1.000."];
            if (Object.keys(this.fieldErrors).length) return;
            this.step = 7;
            this.scrollTop();
        },
        continuePaes() {
            this.fieldErrors = {};
            ["paes_lectora", "paes_m1", "paes_electiva_puntaje"].forEach(field => {
                const value = Number(this.form[field]);
                if (!value || value < 100 || value > 1000) this.fieldErrors[field] = ["Ingresa un puntaje entre 100 y 1.000."];
            });
            if (!this.form.paes_electiva) this.fieldErrors.paes_electiva = ["Selecciona una prueba electiva."];
            if (Object.keys(this.fieldErrors).length) return;
            this.step = 8;
            this.scrollTop();
        },
        async submit() {
            this.fieldErrors = {};
            if (!this.selectedProgram) {
                this.fieldErrors.product_id = ["Selecciona una carrera UGM."];
                if (this.form.grupo !== "pregrado") {
                    this.error = "Selecciona un programa para continuar.";
                    return;
                }
            }

            if (!String(this.form.nombres || "").trim()) {
                this.fieldErrors.nombres = ["Ingresa tus nombres."];
            }
            if (!String(this.form.apellido_paterno || "").trim()) {
                this.fieldErrors.apellido_paterno = ["Ingresa tu apellido paterno."];
            }
            if (!String(this.form.email || "").trim()) {
                this.fieldErrors.email = ["Ingresa tu correo electrónico."];
            } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
                this.fieldErrors.email = ["Ingresa un correo válido."];
            }
            if (!String(this.form.rut || "").trim()) {
                this.fieldErrors.rut = ["Ingresa tu RUT."];
            }
            if (!String(this.form.telefono || "").trim()) {
                this.fieldErrors.telefono = ["Ingresa tu teléfono."];
            }
            if (Object.keys(this.fieldErrors).length) return;

            if (this.form.grupo === "pregrado") {
                [
                    "sexo", "curso", "region_colegio", "comuna_colegio", "colegio",
                    "pueblo_originario", "discapacidad", "product_id", "integrantes_hogar",
                    "ingreso_hogar", "beneficio_estatal", "beca_indigena", "tiene_cae"
                ].forEach(field => {
                    if (!this.form[field]) this.fieldErrors[field] = ["Este campo es obligatorio."];
                });

                const nem = Number(this.form.nem_promedio);
                const ranking = Number(this.form.ranking);
                if (!nem || nem < 4 || nem > 7) {
                    this.fieldErrors.nem_promedio = ["Ingresa un promedio entre 4,0 y 7,0."];
                }
                if (!ranking || ranking < 100 || ranking > 1000) {
                    this.fieldErrors.ranking = ["Ingresa un puntaje entre 100 y 1.000."];
                }
                ["paes_lectora", "paes_m1", "paes_electiva_puntaje"].forEach(field => {
                    const value = Number(this.form[field]);
                    if (!value || value < 100 || value > 1000) {
                        this.fieldErrors[field] = ["Ingresa un puntaje entre 100 y 1.000."];
                    }
                });
                if (!this.form.paes_electiva) {
                    this.fieldErrors.paes_electiva = ["Selecciona una prueba electiva."];
                }
                if (Object.keys(this.fieldErrors).length) return;
            }

            if (!this.form.acepta_contacto) {
                this.fieldErrors = { acepta_contacto: ["Debes autorizar el contacto para solicitar la oferta."] };
                return;
            }

            this.submitting = true;
            this.error = "";
            this.fieldErrors = {};

            try {
                const response = await axios.post("/api/simulador/prospectos", this.form);
                this.result = response.data.quote;
                this.complete = true;
                this.scrollTop();
            } catch (error) {
                if (error.response && error.response.status === 422 && error.response.data.errors) {
                    this.fieldErrors = error.response.data.errors;
                    this.error = "Revisa los datos ingresados antes de continuar.";
                } else {
                    this.error = error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : "No pudimos registrar la simulación. Inténtalo nuevamente.";
                }
            } finally {
                this.submitting = false;
            }
        },
        goTo(step) {
            if (step < this.step) {
                this.step = step;
                this.error = "";
                this.scrollTop();
            }
        },
        restart() {
            this.step = 1;
            this.complete = false;
            this.showContactFields = false;
            this.installments = 10;
            this.result = null;
            this.search = "";
            this.error = "";
            this.fieldErrors = {};
            this.form = {
                nombres: "",
                apellido_paterno: "",
                apellido_materno: "",
                rut: "",
                email: "",
                telefono: "",
                grupo: "",
                product_id: null,
                curso: "",
                region_colegio: "",
                comuna_colegio: "",
                colegio: "",
                beneficio_estatal: "",
                beca_indigena: "",
                tiene_cae: "",
                sexo: "",
                pueblo_originario: "",
                discapacidad: "",
                integrantes_hogar: "",
                ingreso_hogar: "",
                nem_promedio: "",
                ranking: "",
                paes_lectora: "",
                paes_m1: "",
                paes_electiva: "",
                paes_electiva_puntaje: "",
                paes_m2: "",
                acepta_contacto: false
            };
            this.scrollTop();
        },
        fieldError(field) {
            return this.fieldErrors[field] ? this.fieldErrors[field][0] : "";
        },
        formatCurrency(value) {
            return new Intl.NumberFormat("es-CL", {
                style: "currency",
                currency: "CLP",
                maximumFractionDigits: 0
            }).format(Number(value || 0));
        },
        yesNoLabel(value) {
            return { si: "Sí", no: "No", no_se: "No lo sé" }[value] || "No informado";
        },
        sexLabel(value) {
            return {
                femenino: "Femenino",
                masculino: "Masculino",
                otro: "Otro",
                prefiero_no_decir: "Prefiero no decir"
            }[value] || "No informado";
        },
        incomeLabel(value) {
            return {
                menos_500: "Menos de $500.000",
                "500_1000": "$500.000 a $1.000.000",
                "1000_1500": "$1.000.001 a $1.500.000",
                "1500_2000": "$1.500.001 a $2.000.000",
                mas_2000: "Más de $2.000.000",
                no_se: "No lo sé"
            }[value] || "No informado";
        },
        stateBenefitLabel(value) {
            return {
                ninguno: "Ninguno",
                gratuidad: "Gratuidad",
                beca_estatal: "Beca estatal",
                no_se: "No lo sé"
            }[value] || "No informado";
        },
        electiveLabel(value) {
            return value === "historia" ? "Historia y Ciencias Sociales" : value === "ciencias" ? "Ciencias" : "No informada";
        },
        normalize(value) {
            return String(value || "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase();
        },
        scrollTop() {
            this.$nextTick(() => window.scrollTo({ top: 0, behavior: "smooth" }));
        }
    }
};
</script>

<template>
    <main class="simulator-shell">
        <header class="public-header">
            <div class="header-inner">
                <a href="https://www.ugm.cl" aria-label="Universidad Gabriela Mistral">
                    <img :src="logoUrl" alt="Universidad Gabriela Mistral" />
                </a>
                <div class="header-help">
                    <i class="bx bx-lock-alt"></i>
                    Simulación segura y confidencial
                </div>
            </div>
        </header>

        <section class="simulator-content">
            <div v-if="!complete" class="intro">
                <span class="intro-badge">Admisión UGM</span>
                <h1>Simula el arancel de tu próximo programa</h1>
                <p>Primero elige el tipo de programa. Podrás explorar la oferta sin entregar datos de contacto.</p>
            </div>

            <div v-if="!complete" class="progress-wrap" aria-label="Progreso del formulario">
                <button :class="['progress-step', { active: step >= 1 }]" @click="goTo(1)">
                    <span>1</span><small>Tipo de programa</small>
                </button>
                <div :class="['progress-line', { active: step >= 2 }]"></div>
                <button :class="['progress-step', { active: step >= 2 }]" @click="goTo(form.grupo === 'pregrado' ? 2 : 3)">
                    <span>2</span><small>{{ form.grupo === 'pregrado' ? 'Formulario único' : 'Elige tu programa' }}</small>
                </button>
                <div :class="['progress-line', { active: form.grupo === 'pregrado' ? step >= 4 : showContactFields }]"></div>
                <button :class="['progress-step', { active: form.grupo === 'pregrado' ? step >= 4 : showContactFields }]" @click="goTo(form.grupo === 'pregrado' ? 4 : 3)">
                    <span>3</span><small>{{ form.grupo === 'pregrado' ? 'Resultado' : 'Contacto' }}</small>
                </button>
            </div>

            <div v-if="error && !complete" class="alert alert-danger simulator-alert">
                <i class="bx bx-error-circle"></i>{{ error }}
            </div>

            <section v-if="step === 2 && form.grupo === 'pregrado' && !complete" class="form-card wide-card journey-card pregrado-single">
                <div class="journey-heading">
                    <span class="journey-symbol"><i class="bx bx-calculator"></i></span>
                    <div><span class="journey-kicker">Simulación Pregrado UGM</span><h2>Completa tus antecedentes en un solo formulario</h2><p>Todos los campos marcados con * son necesarios para generar la simulación.</p></div>
                </div>
                <div class="section-progress" aria-live="polite">
                    <div><strong>{{ pregradoCompletedSections }} de 5</strong><span>secciones completas</span></div>
                    <div class="section-progress-track"><span :style="{ width: (pregradoCompletedSections * 20) + '%' }"></span></div>
                </div>

                <div :class="['single-section', { 'section-complete': pregradoSection1Complete }]">
                    <div class="single-section-title"><span>01</span><div><h3>Datos personales</h3><p>Información para identificar tu simulación y contactarte.</p></div><small v-if="pregradoSection1Complete" class="section-state"><i class="bx bx-check"></i> Completa</small></div>
                    <div class="form-grid three-columns">
                        <div class="form-group"><label for="nombres">Nombres *</label><input id="nombres" v-model.trim="form.nombres" class="form-control" placeholder="Ej. María José" /><div class="field-message">{{ fieldError('nombres') }}</div></div>
                        <div class="form-group"><label for="apellido_paterno">Apellido paterno *</label><input id="apellido_paterno" v-model.trim="form.apellido_paterno" class="form-control" placeholder="Ej. González" /><div class="field-message">{{ fieldError('apellido_paterno') }}</div></div>
                        <div class="form-group"><label for="apellido_materno">Apellido materno</label><input id="apellido_materno" v-model.trim="form.apellido_materno" class="form-control" placeholder="Ej. Soto" /></div>
                        <div class="form-group"><label for="pregrado-rut">RUT *</label><input id="pregrado-rut" v-model.trim="form.rut" class="form-control" placeholder="12.345.678-9" /><div class="field-message">{{ fieldError('rut') }}</div></div>
                        <div class="form-group"><label for="pregrado-email">Correo electrónico *</label><input id="pregrado-email" v-model.trim="form.email" type="email" class="form-control" placeholder="nombre@correo.cl" /><div class="field-message">{{ fieldError('email') }}</div></div>
                        <div class="form-group"><label for="pregrado-phone">Teléfono *</label><input id="pregrado-phone" v-model.trim="form.telefono" type="tel" class="form-control" placeholder="+56 9 1234 5678" /><div class="field-message">{{ fieldError('telefono') }}</div></div>
                        <div class="form-group"><label for="sexo">Sexo *</label><select id="sexo" v-model="form.sexo" class="form-control"><option value="">Selecciona</option><option value="femenino">Femenino</option><option value="masculino">Masculino</option><option value="otro">Otro</option><option value="prefiero_no_decir">Prefiero no decir</option></select><div class="field-message">{{ fieldError('sexo') }}</div></div>
                    </div>
                </div>

                <div :class="['single-section', { 'section-locked': !pregradoSection1Complete, 'section-complete': pregradoSection2Complete }]">
                    <div class="single-section-title"><span>02</span><div><h3>Colegio y contexto personal</h3><p>Cuéntanos sobre tu etapa escolar actual.</p></div><small v-if="pregradoSection2Complete" class="section-state"><i class="bx bx-check"></i> Completa</small></div>
                    <div v-if="!pregradoSection1Complete" class="section-lock-note"><i class="bx bx-lock-alt"></i> Completa tus datos personales para habilitar esta sección.</div>
                    <fieldset class="section-fields" :disabled="!pregradoSection1Complete">
                    <div class="form-grid three-columns">
                        <div class="form-group"><label for="curso">Curso *</label><select id="curso" v-model="form.curso" class="form-control"><option value="">Selecciona</option><option v-for="course in ['1° medio','2° medio','3° medio','4° medio','Egresado']" :key="course" :value="course">{{ course }}</option></select><div class="field-message">{{ fieldError('curso') }}</div></div>
                        <div class="form-group"><label for="region">Región del colegio *</label><select id="region" v-model="form.region_colegio" class="form-control" @change="changeRegion"><option value="">Selecciona</option><option v-for="region in regions" :key="region" :value="region">{{ region }}</option></select><div class="field-message">{{ fieldError('region_colegio') }}</div></div>
                        <div class="form-group"><label for="comuna">Comuna *</label><select id="comuna" v-model="form.comuna_colegio" class="form-control" :disabled="!form.region_colegio || loadingSchools" @change="changeCommune"><option value="">Selecciona</option><option v-for="commune in communes" :key="commune" :value="commune">{{ commune }}</option></select><div class="field-message">{{ fieldError('comuna_colegio') }}</div></div>
                        <div class="form-group span-2"><label for="colegio">Colegio *</label><select id="colegio" v-model="form.colegio" class="form-control" :disabled="!form.comuna_colegio || loadingSchools"><option value="">Selecciona tu colegio</option><option v-for="school in schools" :key="school.id" :value="school.nombre">{{ school.nombre }}</option></select><div class="field-message">{{ fieldError('colegio') }}</div></div>
                        <div class="form-group"><label for="pueblo-originario">¿Perteneces o desciendes de un pueblo originario? *</label><select id="pueblo-originario" v-model="form.pueblo_originario" class="form-control"><option value="">Selecciona</option><option value="si">Sí</option><option value="no">No</option></select><div class="field-message">{{ fieldError('pueblo_originario') }}</div></div>
                        <div class="form-group"><label for="discapacidad">¿Estás en situación de discapacidad? *</label><select id="discapacidad" v-model="form.discapacidad" class="form-control"><option value="">Selecciona</option><option value="si">Sí</option><option value="no">No</option></select><div class="field-message">{{ fieldError('discapacidad') }}</div></div>
                    </div>
                    </fieldset>
                </div>

                <div :class="['single-section', { 'section-locked': !pregradoSection2Complete, 'section-complete': pregradoSection3Complete }]">
                    <div class="single-section-title"><span>03</span><div><h3>Preferencia y situación del hogar</h3><p>Elige directamente una carrera disponible en UGM.</p></div><small v-if="pregradoSection3Complete" class="section-state"><i class="bx bx-check"></i> Completa</small></div>
                    <div v-if="!pregradoSection2Complete" class="section-lock-note"><i class="bx bx-lock-alt"></i> Completa la sección anterior para elegir tu carrera.</div>
                    <fieldset class="section-fields" :disabled="!pregradoSection2Complete">
                    <div class="form-grid three-columns">
                        <div class="form-group span-2"><label for="pregrado-program">Carrera UGM *</label><select id="pregrado-program" v-model="form.product_id" class="form-control"><option :value="null">Selecciona una carrera</option><option v-for="program in filteredPrograms" :key="program.id" :value="program.id">{{ program.nombre }}</option></select><div class="field-message">{{ fieldError('product_id') }}</div></div>
                        <div class="form-group"><label for="integrantes-hogar">Integrantes del hogar *</label><input id="integrantes-hogar" v-model="form.integrantes_hogar" type="number" min="1" max="20" class="form-control" placeholder="Ej. 4" /><div class="field-message">{{ fieldError('integrantes_hogar') }}</div></div>
                        <div class="form-group span-2"><label for="ingreso-hogar">Ingreso mensual aproximado del hogar *</label><select id="ingreso-hogar" v-model="form.ingreso_hogar" class="form-control"><option value="">Selecciona un rango</option><option value="menos_500">Menos de $500.000</option><option value="500_1000">Entre $500.000 y $1.000.000</option><option value="1000_1500">Entre $1.000.001 y $1.500.000</option><option value="1500_2000">Entre $1.500.001 y $2.000.000</option><option value="mas_2000">Más de $2.000.000</option><option value="no_se">No lo sé</option></select><div class="field-message">{{ fieldError('ingreso_hogar') }}</div></div>
                        <div v-if="selectedProgram" class="selected-career-summary"><small>Carrera seleccionada</small><strong>{{ selectedProgram.nombre }}</strong><span>Arancel referencial: {{ formatCurrency(selectedProgram.arancel) }}</span></div>
                    </div>
                    </fieldset>
                </div>

                <div :class="['single-section', { 'section-locked': !pregradoSection3Complete, 'section-complete': pregradoSection4Complete }]">
                    <div class="single-section-title"><span>04</span><div><h3>Beneficios y financiamiento</h3><p>Indica los beneficios que ya tienes o podrías tener.</p></div><small v-if="pregradoSection4Complete" class="section-state"><i class="bx bx-check"></i> Completa</small></div>
                    <div v-if="!pregradoSection3Complete" class="section-lock-note"><i class="bx bx-lock-alt"></i> Completa la sección anterior para informar tus beneficios.</div>
                    <fieldset class="section-fields" :disabled="!pregradoSection3Complete">
                    <div class="form-grid three-columns">
                        <div class="form-group"><label for="beneficio-estatal">Beneficio estatal *</label><select id="beneficio-estatal" v-model="form.beneficio_estatal" class="form-control"><option value="">Selecciona</option><option value="ninguno">Ninguno</option><option value="gratuidad">Gratuidad</option><option value="beca_estatal">Beca estatal</option><option value="no_se">No lo sé</option></select><div class="field-message">{{ fieldError('beneficio_estatal') }}</div></div>
                        <div class="form-group"><label for="beca-indigena">¿Tienes Beca Indígena? *</label><select id="beca-indigena" v-model="form.beca_indigena" class="form-control"><option value="">Selecciona</option><option value="si">Sí</option><option value="no">No</option><option value="no_se">No lo sé</option></select><div class="field-message">{{ fieldError('beca_indigena') }}</div></div>
                        <div class="form-group"><label for="cae">¿Cuentas con CAE? *</label><select id="cae" v-model="form.tiene_cae" class="form-control"><option value="">Selecciona</option><option value="si">Sí</option><option value="no">No</option><option value="no_se">No lo sé</option></select><div class="field-message">{{ fieldError('tiene_cae') }}</div></div>
                    </div>
                    </fieldset>
                </div>

                <div :class="['single-section', { 'section-locked': !pregradoSection4Complete, 'section-complete': pregradoSection5Complete }]">
                    <div class="single-section-title"><span>05</span><div><h3>Promedio y puntajes</h3><p>Ingresa tus antecedentes académicos y mejores puntajes PAES.</p></div><small v-if="pregradoSection5Complete" class="section-state"><i class="bx bx-check"></i> Completa</small></div>
                    <div v-if="!pregradoSection4Complete" class="section-lock-note"><i class="bx bx-lock-alt"></i> Completa la sección anterior para ingresar tus puntajes.</div>
                    <fieldset class="section-fields" :disabled="!pregradoSection4Complete">
                    <div class="form-grid three-columns">
                        <div class="form-group"><label for="nem">Promedio de Enseñanza Media *</label><input id="nem" v-model="form.nem_promedio" type="number" min="4" max="7" step="0.01" class="form-control" placeholder="Ej. 6.5" /><div class="field-message">{{ fieldError('nem_promedio') }}</div></div>
                        <div class="form-group"><label for="ranking">Puntaje Ranking *</label><input id="ranking" v-model="form.ranking" type="number" min="100" max="1000" class="form-control" placeholder="Ej. 650" /><div class="field-message">{{ fieldError('ranking') }}</div></div>
                        <div class="form-group"><label for="paes-m1">Competencia Matemática 1 *</label><input id="paes-m1" v-model="form.paes_m1" type="number" min="100" max="1000" class="form-control" placeholder="Ej. 650" /><div class="field-message">{{ fieldError('paes_m1') }}</div></div>
                        <div class="form-group"><label for="paes-lectora">Competencia Lectora *</label><input id="paes-lectora" v-model="form.paes_lectora" type="number" min="100" max="1000" class="form-control" placeholder="Ej. 650" /><div class="field-message">{{ fieldError('paes_lectora') }}</div></div>
                        <div class="form-group"><label for="paes-electiva">Mejor prueba electiva *</label><select id="paes-electiva" v-model="form.paes_electiva" class="form-control"><option value="">Selecciona</option><option value="historia">Historia y Ciencias Sociales</option><option value="ciencias">Ciencias</option></select><div class="field-message">{{ fieldError('paes_electiva') }}</div></div>
                        <div class="form-group"><label for="paes-electiva-puntaje">Puntaje electiva *</label><input id="paes-electiva-puntaje" v-model="form.paes_electiva_puntaje" type="number" min="100" max="1000" class="form-control" placeholder="Ej. 650" /><div class="field-message">{{ fieldError('paes_electiva_puntaje') }}</div></div>
                        <div class="form-group"><label for="paes-m2">Competencia Matemática 2 <small>(opcional)</small></label><input id="paes-m2" v-model="form.paes_m2" type="number" min="100" max="1000" class="form-control" placeholder="Ej. 650" /></div>
                    </div>
                    </fieldset>
                </div>

                <div :class="['single-submit-panel', { 'submit-locked': !pregradoSection5Complete }]">
                    <div v-if="selectedProgram" class="single-quote"><span>Arancel anual referencial</span><strong>{{ formatCurrency(offerPrice) }}</strong><small>Los beneficios se evaluarán según sus requisitos.</small><div class="single-installments"><select v-model.number="installments" aria-label="Número de cuotas"><option v-for="option in [3, 6, 10, 12]" :key="option" :value="option">{{ option }} cuotas</option></select><b>{{ formatCurrency(installmentAmount) }} <small>por cuota</small></b></div></div>
                    <div class="single-consent"><label><input v-model="form.acepta_contacto" type="checkbox" :disabled="!pregradoSection5Complete" /> Acepto ser contactado por UGM y autorizo el uso de estos datos para gestionar mi simulación.</label><div class="field-message">{{ fieldError('acepta_contacto') }}</div></div>
                    <button type="button" class="primary-button" :disabled="submitting || !pregradoSection5Complete" @click="submit"><i :class="submitting ? 'bx bx-loader-alt bx-spin' : (!pregradoSection5Complete ? 'bx bx-lock-alt' : 'bx bx-calculator')"></i>{{ submitting ? 'Calculando...' : (pregradoSection5Complete ? 'Simular beneficio' : 'Completa las secciones') }}</button>
                </div>
                <div class="card-actions"><button type="button" class="back-button" @click="goTo(1)"><i class="bx bx-left-arrow-alt"></i> Cambiar tipo de programa</button></div>
            </section>

            <section v-if="step === 1 && !complete" class="form-card">
                <div class="card-heading">
                    <span class="heading-icon"><i class="bx bx-layer"></i></span>
                    <div>
                        <h2>¿Qué tipo de programa buscas?</h2>
                        <p>Selecciona una alternativa para ver los programas y aranceles disponibles.</p>
                    </div>
                </div>

                <div class="type-grid">
                    <button v-for="type in programTypes" :key="type.key" type="button" class="type-card" @click="selectType(type)">
                        <span class="type-icon"><i :class="['bx', type.icon]"></i></span>
                        <h3>{{ type.title }}</h3>
                        <p>{{ type.description }}</p>
                        <span class="type-link">Ver programas <i class="bx bx-right-arrow-alt"></i></span>
                    </button>
                </div>

            </section>

            <section v-if="step === 3 && form.grupo !== 'pregrado' && !complete" class="form-card wide-card">
                <div class="card-heading">
                    <span class="heading-icon"><i class="bx bx-search-alt"></i></span>
                    <div>
                        <h2>Elige tu {{ form.grupo === "advance" ? "carrera Advance" : "programa" }}</h2>
                        <p>{{ currentType ? currentType.title : "" }} · Todos los valores están expresados en pesos chilenos.</p>
                    </div>
                </div>

                <div v-if="loadingPrograms" class="loading-box"><i class="bx bx-loader-alt bx-spin"></i> Cargando programas...</div>
                <template v-else>
                    <div class="program-layout">
                        <div class="program-picker">
                            <div class="program-search">
                                <i class="bx bx-search"></i>
                                <input v-model="search" class="form-control" type="search" placeholder="Buscar por nombre..." />
                            </div>
                            <div class="program-list">
                                <button
                                    v-for="program in filteredPrograms"
                                    :key="program.id"
                                    type="button"
                                    :class="['program-option', { selected: Number(form.product_id) === Number(program.id) }]"
                                    @click="selectProgram(program)"
                                >
                                    <span class="radio-mark"><i class="bx bx-check"></i></span>
                                    <span class="program-copy">
                                        <strong>{{ program.nombre }}</strong>
                                        <small>Arancel referencial: {{ formatCurrency(program.arancel) }}</small>
                                    </span>
                                </button>
                                <div v-if="!filteredPrograms.length" class="no-results">No encontramos programas con esa búsqueda.</div>
                            </div>
                        </div>

                        <aside :class="['quote-card', { ready: selectedProgram }]">
                            <div class="discount-ribbon">{{ form.grupo === 'advance' ? '35% de beneficio online' : 'Arancel referencial' }}</div>
                            <template v-if="selectedProgram">
                                <span class="quote-label">Programa seleccionado</span>
                                <h3>{{ selectedProgram.nombre }}</h3>
                                <div class="price-row old-price"><span>Arancel referencial</span><strong>{{ formatCurrency(selectedProgram.arancel) }}</strong></div>
                                <div v-if="form.grupo === 'advance'" class="price-row saving"><span>Beneficio 35%</span><strong>-{{ formatCurrency(savings) }}</strong></div>
                                <div class="offer-total">
                                    <span>{{ form.grupo === 'advance' ? 'Oferta especial online' : 'Arancel referencial' }}</span>
                                    <strong>{{ formatCurrency(offerPrice) }}</strong>
                                </div>
                                <div class="installment-simulator">
                                    <div class="installment-heading"><span>Simula tus cuotas</span><small>División referencial del monto</small></div>
                                    <div class="installment-options">
                                        <button v-for="option in [3, 6, 10, 12]" :key="option" type="button" :class="{ active: installments === option }" @click="installments = option">{{ option }}</button>
                                    </div>
                                    <div class="installment-result"><span>{{ installments }} cuotas de</span><strong>{{ formatCurrency(installmentAmount) }}</strong></div>
                                    <small class="installment-note">No incluye intereses, costos financieros ni condiciones de matrícula.</small>
                                </div>
                                <p class="quote-note"><i class="bx bx-info-circle"></i> Valor referencial sujeto a validación y condiciones de admisión. Un asesor confirmará la oferta.</p>
                                <button v-if="!showContactFields" type="button" class="primary-button submit-button" @click="requestContact">
                                    <i class="bx bx-message-rounded-detail"></i>
                                    {{ form.grupo === "advance" ? "Quiero más información" : "Quiero que me contacte un asesor" }}
                                </button>
                            </template>
                            <div v-else class="quote-placeholder">
                                <i class="bx bx-pointer"></i>
                                <h3>Selecciona un programa</h3>
                                <p>Aquí verás el arancel disponible para el programa seleccionado.</p>
                            </div>
                        </aside>
                    </div>
                    <div v-if="showContactFields && selectedProgram" class="contact-request-wide">
                        <div class="wide-contact-heading"><span><i class="bx bx-phone-call"></i></span><div><small>Orientación personalizada</small><h3>¿Dónde te contactamos?</h3><p>Completa tus datos y un asesor UGM podrá entregarte más información sobre {{ selectedProgram.nombre }}.</p></div></div>
                        <div class="wide-contact-grid">
                            <div class="form-group"><label for="contact-names">Nombres *</label><input id="contact-names" v-model.trim="form.nombres" :class="['form-control', { 'is-invalid': fieldError('nombres') }]" autocomplete="given-name" placeholder="Ej. María José" /><div class="invalid-feedback">{{ fieldError("nombres") }}</div></div>
                            <div class="form-group"><label for="contact-last-name">Apellido paterno *</label><input id="contact-last-name" v-model.trim="form.apellido_paterno" :class="['form-control', { 'is-invalid': fieldError('apellido_paterno') }]" autocomplete="family-name" placeholder="Ej. González" /><div class="invalid-feedback">{{ fieldError("apellido_paterno") }}</div></div>
                            <div class="form-group"><label for="contact-second-last-name">Apellido materno <small>opcional</small></label><input id="contact-second-last-name" v-model.trim="form.apellido_materno" class="form-control" autocomplete="additional-name" placeholder="Ej. Soto" /></div>
                            <div class="form-group"><label for="contact-email">Correo electrónico *</label><input id="contact-email" ref="contactEmail" v-model.trim="form.email" :class="['form-control', { 'is-invalid': fieldError('email') }]" type="email" autocomplete="email" placeholder="nombre@correo.cl" /><div class="invalid-feedback">{{ fieldError("email") }}</div></div>
                            <div class="form-group"><label for="contact-rut">RUT *</label><input id="contact-rut" v-model.trim="form.rut" :class="['form-control', { 'is-invalid': fieldError('rut') }]" autocomplete="off" placeholder="12.345.678-9" /><div class="invalid-feedback">{{ fieldError("rut") }}</div></div>
                            <div class="form-group"><label for="contact-phone">Teléfono *</label><input id="contact-phone" v-model.trim="form.telefono" :class="['form-control', { 'is-invalid': fieldError('telefono') }]" type="tel" autocomplete="tel" placeholder="+56 9 1234 5678" /><div class="invalid-feedback">{{ fieldError("telefono") }}</div></div>
                        </div>
                        <div class="wide-contact-footer">
                            <div><label class="wide-consent"><input v-model="form.acepta_contacto" type="checkbox" /><span>Autorizo a UGM a contactarme por teléfono o correo para orientarme sobre este programa.</span></label><div v-if="fieldError('acepta_contacto')" class="field-message">{{ fieldError("acepta_contacto") }}</div></div>
                            <button type="button" class="primary-button" :disabled="submitting" @click="submit"><i :class="submitting ? 'bx bx-loader-alt bx-spin' : 'bx bx-send'"></i>{{ submitting ? "Enviando..." : "Solicitar contacto" }}</button>
                        </div>
                    </div>
                </template>

                <div class="card-actions">
                    <button type="button" class="back-button" @click="goTo(1)"><i class="bx bx-left-arrow-alt"></i> Cambiar tipo de programa</button>
                </div>
            </section>

            <section v-if="step === 4 && !complete" class="form-card journey-card">
                <div class="journey-heading">
                    <span class="journey-symbol"><i class="bx bx-buildings"></i></span>
                    <div><span class="journey-kicker">Tu ruta UGM · Colegio</span><h2>Cuéntanos dónde estás estudiando</h2><p>Esta información nos ayuda a contextualizar tus antecedentes académicos.</p></div>
                </div>
                <div class="question-label"><span>01</span><h3>¿En qué curso estás actualmente?</h3></div>
                <div class="course-grid">
                    <button v-for="course in ['1° medio','2° medio','3° medio','4° medio','Egresado']" :key="course" type="button" :class="['course-button', { selected: form.curso === course }]" @click="form.curso = course"><span class="select-dot"><i class="bx bx-check"></i></span>{{ course }}</button>
                </div>
                <div v-if="fieldError('curso')" class="field-message">{{ fieldError("curso") }}</div>
                <div class="question-label question-label-spaced"><span>02</span><h3>¿Cuál es tu colegio?</h3></div>
                <div class="form-grid school-fields">
                    <div class="form-group">
                        <label>Región</label>
                        <select v-model="form.region_colegio" class="form-control" @change="changeRegion"><option value="">Selecciona tu región</option><option v-for="region in regions" :key="region" :value="region">{{ region }}</option></select>
                        <div class="field-message">{{ fieldError("region_colegio") }}</div>
                    </div>
                    <div class="form-group">
                        <label>Comuna</label>
                        <select v-model="form.comuna_colegio" class="form-control" :disabled="!form.region_colegio || loadingSchools" @change="changeCommune"><option value="">Selecciona tu comuna</option><option v-for="commune in communes" :key="commune" :value="commune">{{ commune }}</option></select>
                        <div class="field-message">{{ fieldError("comuna_colegio") }}</div>
                    </div>
                    <div class="form-group span-2">
                        <label>Colegio</label>
                        <select v-model="form.colegio" class="form-control" :disabled="!form.comuna_colegio || loadingSchools"><option value="">Selecciona tu colegio</option><option v-for="school in schools" :key="school.id" :value="school.nombre">{{ school.nombre }}</option></select>
                        <div class="field-message">{{ fieldError("colegio") }}</div>
                    </div>
                </div>
                <div class="card-actions"><button class="back-button" @click="goTo(2)"><i class="bx bx-left-arrow-alt"></i> Anterior</button><button class="primary-button" @click="continueAcademic">Siguiente <i class="bx bx-right-arrow-alt"></i></button></div>
            </section>

            <section v-if="step === 5 && !complete" class="form-card journey-card narrow-card">
                <div class="journey-heading">
                    <span class="journey-symbol"><i class="bx bx-heart"></i></span>
                    <div><span class="journey-kicker">Tu ruta UGM · Apoyo estudiantil</span><h2>Beneficios y financiamiento</h2><p>No necesitas conocer todos los detalles; puedes indicar “No lo sé”.</p></div>
                </div>
                <div class="benefit-question"><div class="question-label"><span>01</span><h3>¿Tienes algún beneficio estatal?</h3></div><div class="choice-grid"><button v-for="option in [{v:'ninguno',l:'Ninguno'},{v:'gratuidad',l:'Gratuidad'},{v:'beca_estatal',l:'Beca estatal'},{v:'no_se',l:'No lo sé'}]" :key="option.v" type="button" :class="['choice-button',{selected:form.beneficio_estatal===option.v}]" @click="form.beneficio_estatal=option.v"><span class="select-dot"><i class="bx bx-check"></i></span>{{ option.l }}</button></div><div class="field-message">{{ fieldError('beneficio_estatal') }}</div></div>
                <div class="benefit-question"><div class="question-label"><span>02</span><h3>¿Tienes Beca Indígena?</h3></div><div class="choice-grid three"><button v-for="option in [{v:'si',l:'Sí'},{v:'no',l:'No'},{v:'no_se',l:'No lo sé'}]" :key="option.v" type="button" :class="['choice-button',{selected:form.beca_indigena===option.v}]" @click="form.beca_indigena=option.v"><span class="select-dot"><i class="bx bx-check"></i></span>{{ option.l }}</button></div><div class="field-message">{{ fieldError('beca_indigena') }}</div></div>
                <div class="benefit-question"><div class="question-label"><span>03</span><h3>¿Cuentas con CAE?</h3></div><div class="choice-grid three"><button v-for="option in [{v:'si',l:'Sí'},{v:'no',l:'No'},{v:'no_se',l:'No lo sé'}]" :key="option.v" type="button" :class="['choice-button',{selected:form.tiene_cae===option.v}]" @click="form.tiene_cae=option.v"><span class="select-dot"><i class="bx bx-check"></i></span>{{ option.l }}</button></div><div class="field-message">{{ fieldError('tiene_cae') }}</div></div>
                <div class="card-actions"><button class="back-button" @click="goTo(4)"><i class="bx bx-left-arrow-alt"></i> Anterior</button><button class="primary-button" @click="continueBenefits">Siguiente <i class="bx bx-right-arrow-alt"></i></button></div>
            </section>

            <section v-if="step === 6 && !complete" class="form-card journey-card narrow-card">
                <div class="journey-heading">
                    <span class="journey-symbol"><i class="bx bx-line-chart"></i></span>
                    <div><span class="journey-kicker">Tu ruta UGM · Trayectoria</span><h2>Tus antecedentes académicos</h2><p>Usaremos estos datos sólo como una referencia inicial para tu simulación.</p></div>
                </div>
                <div class="academic-question"><div class="question-label"><span>01</span><h3>¿Cuál es tu promedio NEM?</h3></div><div class="form-group academic-input"><input v-model="form.nem_promedio" aria-label="Promedio NEM" type="number" min="4" max="7" step="0.01" class="form-control" placeholder="Ej: 6.5" /><small>Promedio de todos los cursos de 1° a 4° medio.</small><div class="field-message">{{ fieldError("nem_promedio") }}</div></div></div>
                <div class="academic-question"><div class="question-label"><span>02</span><h3>¿Cuál es tu Ranking de notas?</h3></div><div class="form-group academic-input"><input v-model="form.ranking" aria-label="Ranking de notas" type="number" min="100" max="1000" class="form-control" placeholder="Ej: 650" /><small>Si aún no lo conoces, ingresa una estimación entre 100 y 1.000.</small><div class="field-message">{{ fieldError("ranking") }}</div></div></div>
                <div class="card-actions"><button class="back-button" @click="goTo(5)"><i class="bx bx-left-arrow-alt"></i> Anterior</button><button class="primary-button" @click="continueScores">Siguiente <i class="bx bx-right-arrow-alt"></i></button></div>
            </section>

            <section v-if="step === 7 && !complete" class="form-card journey-card wide-card">
                <div class="journey-heading">
                    <span class="journey-symbol"><i class="bx bx-pencil"></i></span>
                    <div><span class="journey-kicker">Tu ruta UGM · Evaluación</span><h2>Registra tus puntajes PAES</h2><p>Con estos datos podrás avanzar a la elección de tu carrera.</p></div>
                </div>
                <div class="program-layout">
                    <div class="paes-form">
                        <div class="form-grid">
                            <div class="form-group"><label>Competencia Lectora</label><input v-model="form.paes_lectora" type="number" min="100" max="1000" class="form-control" /><div class="field-message">{{ fieldError("paes_lectora") }}</div></div>
                            <div class="form-group"><label>Matemática 1 (M1)</label><input v-model="form.paes_m1" type="number" min="100" max="1000" class="form-control" /><div class="field-message">{{ fieldError("paes_m1") }}</div></div>
                            <div class="form-group span-2"><label>Selecciona tu mejor prueba electiva</label><div class="elective-options"><label><input v-model="form.paes_electiva" type="radio" value="historia" /> Historia y Ciencias Sociales</label><label><input v-model="form.paes_electiva" type="radio" value="ciencias" /> Ciencias</label></div><div class="field-message">{{ fieldError("paes_electiva") }}</div></div>
                            <div class="form-group"><label>Puntaje prueba electiva</label><input v-model="form.paes_electiva_puntaje" type="number" min="100" max="1000" class="form-control" /><div class="field-message">{{ fieldError("paes_electiva_puntaje") }}</div></div>
                            <div class="form-group"><label>Matemática 2 (M2) <small>opcional</small></label><input v-model="form.paes_m2" type="number" min="100" max="1000" class="form-control" /></div>
                        </div>
                    </div>
                    <aside class="academic-summary"><i class="bx bx-info-circle"></i><h3>Ya casi terminamos</h3><p>Después de ingresar tus puntajes podrás elegir la carrera que quieres simular y ver su arancel.</p></aside>
                </div>
                <div class="card-actions"><button class="back-button" @click="goTo(6)"><i class="bx bx-left-arrow-alt"></i> Anterior</button><button class="primary-button" @click="continuePaes">Elegir mi carrera <i class="bx bx-right-arrow-alt"></i></button></div>
            </section>

            <section v-if="complete && result && result.group === 'pregrado'" class="result-sheet">
                <div class="result-hero">
                    <div class="result-finish"><i class="bx bx-check"></i> Simulación completada</div>
                    <span class="result-kicker">Tu escenario UGM</span>
                    <h1>Hola, {{ form.nombres }}</h1>
                    <p>Este es el resumen de los antecedentes que ingresaste y una proyección referencial para la carrera seleccionada.</p>
                    <div class="result-score">
                        <small>Promedio referencial de puntajes informados</small>
                        <strong>{{ Math.round(result.reference_score || 0) }}</strong>
                        <span>puntos</span>
                    </div>
                    <div class="mockup-notice"><i class="bx bx-info-circle"></i><span><b>Resultado demostrativo.</b> No corresponde a una ponderación oficial, adjudicación de beca ni evaluación formal de admisión.</span></div>
                </div>

                <div class="result-program">
                    <span>Carrera UGM seleccionada</span>
                    <h2>{{ result.program }}</h2>
                </div>

                <div class="result-grid">
                    <article class="result-panel">
                        <div class="result-panel-title"><span>01</span><div><h3>Datos personales</h3><p>Información registrada</p></div></div>
                        <dl class="result-list">
                            <div><dt>Nombre</dt><dd>{{ fullName }}</dd></div>
                            <div><dt>RUT</dt><dd>{{ form.rut }}</dd></div>
                            <div><dt>Correo</dt><dd>{{ form.email }}</dd></div>
                            <div><dt>Teléfono</dt><dd>{{ form.telefono }}</dd></div>
                            <div><dt>Sexo</dt><dd>{{ sexLabel(form.sexo) }}</dd></div>
                        </dl>
                    </article>

                    <article class="result-panel">
                        <div class="result-panel-title"><span>02</span><div><h3>Colegio y contexto</h3><p>Antecedentes declarados</p></div></div>
                        <dl class="result-list">
                            <div><dt>Curso</dt><dd>{{ form.curso }}</dd></div>
                            <div><dt>Colegio</dt><dd>{{ form.colegio }}</dd></div>
                            <div><dt>Comuna</dt><dd>{{ form.comuna_colegio }}</dd></div>
                            <div><dt>Región</dt><dd>{{ form.region_colegio }}</dd></div>
                            <div><dt>Pueblo originario</dt><dd>{{ yesNoLabel(form.pueblo_originario) }}</dd></div>
                            <div><dt>Situación de discapacidad</dt><dd>{{ yesNoLabel(form.discapacidad) }}</dd></div>
                        </dl>
                    </article>

                    <article class="result-panel">
                        <div class="result-panel-title"><span>03</span><div><h3>Información académica</h3><p>Notas y pruebas ingresadas</p></div></div>
                        <dl class="result-list score-list">
                            <div><dt>Promedio NEM</dt><dd>{{ form.nem_promedio }}</dd></div>
                            <div><dt>Puntaje Ranking</dt><dd>{{ form.ranking }}</dd></div>
                            <div><dt>Competencia Lectora</dt><dd>{{ form.paes_lectora }}</dd></div>
                            <div><dt>Matemática 1 (M1)</dt><dd>{{ form.paes_m1 }}</dd></div>
                            <div><dt>{{ electiveLabel(form.paes_electiva) }}</dt><dd>{{ form.paes_electiva_puntaje }}</dd></div>
                            <div><dt>Matemática 2 (M2)</dt><dd>{{ form.paes_m2 || 'No informada' }}</dd></div>
                        </dl>
                    </article>

                    <article class="result-panel">
                        <div class="result-panel-title"><span>04</span><div><h3>Hogar y beneficios</h3><p>Información para la orientación</p></div></div>
                        <dl class="result-list">
                            <div><dt>Integrantes del hogar</dt><dd>{{ form.integrantes_hogar }}</dd></div>
                            <div><dt>Ingreso mensual</dt><dd>{{ incomeLabel(form.ingreso_hogar) }}</dd></div>
                            <div><dt>Beneficio estatal</dt><dd>{{ stateBenefitLabel(form.beneficio_estatal) }}</dd></div>
                            <div><dt>Beca Indígena</dt><dd>{{ yesNoLabel(form.beca_indigena) }}</dd></div>
                            <div><dt>CAE</dt><dd>{{ yesNoLabel(form.tiene_cae) }}</dd></div>
                        </dl>
                    </article>
                </div>

                <div class="result-values">
                    <div class="values-heading"><span>05</span><div><h3>Proyección de valores</h3><p>Maqueta referencial basada en el arancel disponible</p></div></div>
                    <div class="values-columns">
                        <div class="value-block regular-values">
                            <span>Valores regulares</span>
                            <div><small>Matrícula referencial</small><strong>{{ formatCurrency(result.enrollment) }}</strong></div>
                            <div><small>Arancel anual referencial</small><strong>{{ formatCurrency(result.list_price) }}</strong></div>
                        </div>
                        <div class="value-block benefit-values">
                            <span>Escenario referencial</span>
                            <div><small>Beneficios declarados</small><strong class="pending-value">Sujetos a evaluación</strong></div>
                            <div class="annual-total"><small>Arancel anual sin beneficios</small><strong>{{ formatCurrency(result.list_price) }}</strong></div>
                            <div><small>Referencia mensual en 10 cuotas</small><strong>{{ formatCurrency(monthlyOffer) }}</strong></div>
                        </div>
                    </div>
                    <p class="values-disclaimer">Los valores son una maqueta de orientación y pueden variar según condiciones de admisión, beneficios aplicables y forma de pago. Un asesor UGM debe confirmar cualquier oferta.</p>
                </div>

                <div class="result-actions">
                    <button type="button" class="primary-button" @click="restart"><i class="bx bx-refresh"></i> Realizar otra simulación</button>
                    <a href="https://www.ugm.cl" class="site-link">Conoce más sobre UGM <i class="bx bx-link-external"></i></a>
                </div>
            </section>

            <section v-else-if="complete" class="success-card">
                <div class="success-icon"><i class="bx bx-check"></i></div>
                <span class="success-eyebrow">Simulación completada</span>
                <h1>¡Tu simulación está lista!</h1>
                <p>Estos resultados son referenciales y corresponden únicamente a esta simulación.</p>
                <div v-if="result" class="success-quote">
                    <span>{{ result.program }}</span>
                    <template v-if="result.group === 'pregrado' && result.reference_score">
                        <small>Promedio de puntajes informados</small>
                        <strong>{{ Math.round(result.reference_score) }} puntos</strong>
                        <small class="score-disclaimer">Este promedio es referencial y no corresponde a la ponderación oficial de postulación.</small>
                    </template>
                    <div v-if="result.enrollment" class="result-enrollment">Matrícula referencial: {{ formatCurrency(result.enrollment) }}</div>
                    <small>{{ result.group === 'advance' ? 'Oferta especial por contacto online' : 'Arancel referencial' }}</small>
                    <strong>{{ formatCurrency(result.offer_price) }}</strong>
                    <div v-if="result.group === 'advance'"><s>{{ formatCurrency(result.list_price) }}</s> <b>35% de beneficio</b></div>
                </div>
                <button type="button" class="primary-button" @click="restart"><i class="bx bx-refresh"></i> Realizar otra simulación</button>
                <a href="https://www.ugm.cl" class="site-link">Conoce más sobre UGM <i class="bx bx-link-external"></i></a>
            </section>
        </section>

        <footer class="public-footer">
            <span>Universidad Gabriela Mistral</span>
            <span>Simulación referencial · Los valores finales están sujetos a validación institucional.</span>
        </footer>
    </main>
</template>

<style scoped>
.simulator-shell { min-height: 100vh; background: radial-gradient(circle at 15% 10%, #f0f6ff 0, #f7f9fc 35%, #f4f6fa 100%); color: #20304a; font-family: Arial, sans-serif; }
.public-header { height: 78px; background: #fff; border-bottom: 1px solid #e9edf3; display: flex; align-items: center; }
.header-inner { width: min(1120px, calc(100% - 40px)); margin: auto; display: flex; justify-content: space-between; align-items: center; }
.header-inner img { width: 190px; max-height: 54px; object-fit: contain; }
.header-help { color: #66758d; font-size: 13px; display: flex; align-items: center; gap: 7px; }
.header-help i { color: #b89445; font-size: 18px; }
.simulator-content { width: min(1040px, calc(100% - 32px)); margin: auto; padding: 48px 0 70px; }
.intro { text-align: center; max-width: 760px; margin: 0 auto 34px; }
.intro-badge, .success-eyebrow { display: inline-block; color: #9a7628; background: #fbf4df; border-radius: 999px; padding: 6px 13px; font-size: 11px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; }
.intro h1, .success-card h1 { color: #142e58; font-size: 38px; letter-spacing: -.6px; margin: 14px 0 10px; font-weight: 750; }
.intro p { color: #68778e; font-size: 17px; line-height: 1.6; }
.progress-wrap { max-width: 640px; margin: 0 auto 30px; display: flex; align-items: flex-start; justify-content: center; }
.progress-step { border: 0; background: transparent; color: #9aa5b5; display: flex; flex-direction: column; align-items: center; gap: 7px; min-width: 110px; font-size: 12px; }
.progress-step span { width: 34px; height: 34px; display: grid; place-items: center; border: 2px solid #dce2ea; border-radius: 50%; background: #fff; font-weight: 700; }
.progress-step.active { color: #183d72; font-weight: 700; }
.progress-step.active span { background: #183d72; border-color: #183d72; color: #fff; box-shadow: 0 4px 12px rgba(24,61,114,.22); }
.progress-line { width: 100px; height: 2px; background: #dce2ea; margin-top: 16px; }
.progress-line.active { background: #183d72; }
.form-card, .success-card { background: #fff; border: 1px solid #e8edf3; border-radius: 20px; padding: 34px 38px; box-shadow: 0 16px 44px rgba(31, 52, 83, .09); max-width: 820px; margin: auto; }
.wide-card { max-width: 1040px; }
.card-heading { display: flex; gap: 14px; align-items: flex-start; padding-bottom: 24px; margin-bottom: 26px; border-bottom: 1px solid #edf0f4; }
.heading-icon { flex: 0 0 45px; width: 45px; height: 45px; display: grid; place-items: center; border-radius: 12px; background: #edf4fc; color: #1a4c87; font-size: 23px; }
.card-heading h2 { color: #17345f; margin: 1px 0 5px; font-size: 23px; }
.card-heading p { color: #758298; margin: 0; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.span-2 { grid-column: span 2; }
.form-group label { color: #34445d; font-size: 13px; font-weight: 700; }
.form-group label b { color: #b8882e; }
.form-control { height: 46px; border: 1px solid #dce3ec; border-radius: 10px; padding: 10px 13px; color: #23344e; }
.form-control:focus { border-color: #3269a5; box-shadow: 0 0 0 3px rgba(50,105,165,.12); }
.card-actions { margin-top: 28px; display: flex; align-items: center; justify-content: space-between; }
.card-actions.right { justify-content: flex-end; }
.primary-button, .back-button { border: 0; border-radius: 10px; padding: 12px 22px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 7px; transition: transform .15s, box-shadow .15s; }
.primary-button { color: #fff; background: linear-gradient(115deg, #153865, #1e568f); box-shadow: 0 6px 15px rgba(23,66,113,.22); }
.primary-button:hover { transform: translateY(-1px); box-shadow: 0 9px 20px rgba(23,66,113,.28); }
.primary-button:disabled { opacity: .65; transform: none; }
.back-button { color: #53657d; background: #eff2f6; }
.type-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.type-card { text-align: left; border: 1px solid #e0e6ee; border-radius: 15px; background: #fff; padding: 24px 21px; min-height: 245px; display: flex; flex-direction: column; transition: all .2s; }
.type-card:hover { border-color: #2b639e; transform: translateY(-3px); box-shadow: 0 12px 24px rgba(31,69,112,.12); }
.type-icon { width: 50px; height: 50px; display: grid; place-items: center; border-radius: 13px; color: #17497f; background: #eaf2fb; font-size: 27px; }
.type-card h3 { color: #18375f; margin: 20px 0 8px; font-size: 20px; }
.type-card p { color: #748196; font-size: 13px; line-height: 1.55; flex-grow: 1; }
.type-link { color: #9b7429; font-weight: 700; font-size: 13px; }
.program-layout { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(300px, .65fr); gap: 24px; }
.program-search { position: relative; margin-bottom: 12px; }
.program-search i { position: absolute; left: 14px; top: 13px; color: #8190a5; font-size: 19px; }
.program-search input { padding-left: 42px; }
.program-list { border: 1px solid #e2e7ee; border-radius: 13px; max-height: 440px; overflow-y: auto; }
.program-option { width: 100%; border: 0; border-bottom: 1px solid #edf0f4; background: #fff; text-align: left; padding: 14px; display: flex; gap: 12px; align-items: center; }
.program-option:first-child { border-radius: 13px 13px 0 0; }
.program-option:last-child { border-bottom: 0; border-radius: 0 0 13px 13px; }
.program-option:hover { background: #f7faff; }
.program-option.selected { background: #edf5ff; }
.radio-mark { flex: 0 0 23px; width: 23px; height: 23px; border: 2px solid #cbd5e2; border-radius: 50%; display: grid; place-items: center; color: transparent; }
.program-option.selected .radio-mark { color: #fff; background: #1b558f; border-color: #1b558f; }
.program-copy { display: flex; flex-direction: column; gap: 3px; }
.program-copy strong { color: #293d59; font-size: 14px; }
.program-copy small { color: #8390a2; }
.no-results, .loading-box { padding: 40px 20px; text-align: center; color: #7c899b; }
.quote-card { position: relative; overflow: hidden; border-radius: 16px; padding: 52px 24px 23px; background: #f6f8fb; border: 1px solid #e2e7ee; min-height: 380px; }
.quote-card.ready { background: linear-gradient(150deg, #122f59 0%, #194b7e 100%); color: #fff; border-color: transparent; }
.discount-ribbon { position: absolute; top: 0; left: 0; right: 0; padding: 10px; text-align: center; background: #d6b35c; color: #26334a; font-size: 12px; font-weight: 800; letter-spacing: .4px; text-transform: uppercase; }
.quote-label { color: #9ec5eb; font-size: 11px; text-transform: uppercase; letter-spacing: .8px; font-weight: 700; }
.quote-card h3 { color: #fff; font-size: 18px; margin: 7px 0 22px; line-height: 1.35; }
.price-row { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; padding: 8px 0; }
.old-price strong { text-decoration: line-through; color: #bfcee0; }
.saving { color: #e9d390; border-bottom: 1px solid rgba(255,255,255,.15); }
.offer-total { padding: 16px 0; display: flex; flex-direction: column; }
.offer-total span { color: #bed2e7; font-size: 12px; }
.offer-total strong { font-size: 32px; color: #fff; letter-spacing: -.5px; }
.installment-simulator { margin: 4px 0 16px; padding: 14px; border: 1px solid rgba(255,255,255,.16); border-radius: 5px 14px 5px 14px; background: rgba(255,255,255,.07); }
.installment-heading { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; margin-bottom: 10px; }
.installment-heading span { color: #fff; font-size: 12px; font-weight: 800; }
.installment-heading small { color: #a9bfd7; font-size: 8px; }
.installment-options { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; }
.installment-options button { border: 1px solid rgba(255,255,255,.22); border-radius: 5px; padding: 6px 3px; background: transparent; color: #c7d6e6; font-size: 10px; font-weight: 800; }
.installment-options button.active { border-color: #d8ae4c; background: #d8ae4c; color: #173047; }
.installment-result { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; margin-top: 10px; }
.installment-result span { color: #b9cbe0; font-size: 9px; }
.installment-result strong { color: #fff; font-size: 18px; }
.installment-note { display: block; margin-top: 5px; color: #91a9c2; font-size: 7px; line-height: 1.35; }
.quote-note { color: #b9cbe0; font-size: 10px; line-height: 1.45; display: flex; gap: 5px; }
.consent-box { display: flex; gap: 9px; align-items: flex-start; color: #d5e0ec; font-size: 10px; line-height: 1.45; cursor: pointer; margin: 16px 0 4px; }
.consent-box input { margin-top: 2px; }
.consent-error { color: #ffd1d1; font-size: 10px; }
.submit-button { width: 100%; margin-top: 16px; background: #fff; color: #174879; box-shadow: none; }
.contact-request { margin-top: 18px; padding-top: 18px; border-top: 1px solid rgba(255,255,255,.18); }
.contact-request-heading { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 15px; }
.contact-request-heading > i { flex: 0 0 31px; width: 31px; height: 31px; display: grid; place-items: center; border-radius: 4px 10px 4px 10px; background: #d8ae4c; color: #173047; font-size: 18px; }
.contact-request-heading div { display: flex; flex-direction: column; gap: 2px; }
.contact-request-heading strong { color: #fff; font-size: 13px; }
.contact-request-heading small { color: #b9cbe0; font-size: 10px; line-height: 1.35; }
.contact-request > label:not(.consent-box) { display: block; margin: 9px 0 4px; color: #dce7f2; font-size: 10px; font-weight: 700; }
.contact-control { width: 100%; height: 40px; border: 1px solid rgba(255,255,255,.24); border-radius: 7px; padding: 8px 10px; background: rgba(255,255,255,.1); color: #fff; outline: none; }
.contact-control::placeholder { color: #9fb3ca; }
.contact-control:focus { border-color: #d8ae4c; box-shadow: 0 0 0 3px rgba(216,174,76,.16); }
.contact-control.invalid { border-color: #ff9e9e; }
.contact-error { margin-top: 3px; color: #ffd1d1; font-size: 10px; }
.contact-submit { margin-top: 12px; }
.contact-request-wide { margin-top: 24px; padding: 27px; border: 1px solid #dfe6ee; border-radius: 6px 22px 6px 22px; background: #f7f9fc; box-shadow: 0 12px 30px rgba(24, 56, 94, .08); }
.wide-contact-heading { display: flex; gap: 15px; align-items: center; padding-bottom: 18px; margin-bottom: 20px; border-bottom: 1px solid #e1e7ee; }
.wide-contact-heading > span { flex: 0 0 48px; width: 48px; height: 48px; display: grid; place-items: center; border-radius: 16px 5px 16px 5px; background: #173f70; color: #fff; font-size: 23px; box-shadow: 5px 5px 0 #f0dfb4; }
.wide-contact-heading small { color: #9a7526; font-size: 9px; font-weight: 900; letter-spacing: .8px; text-transform: uppercase; }
.wide-contact-heading h3 { margin: 2px 0 3px; color: #17345f; font-size: 20px; }
.wide-contact-heading p { margin: 0; color: #758298; font-size: 12px; }
.wide-contact-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.wide-contact-footer { display: grid; grid-template-columns: 1fr auto; gap: 24px; align-items: center; margin-top: 22px; padding-top: 18px; border-top: 1px solid #e1e7ee; }
.wide-consent { display: flex; gap: 9px; align-items: flex-start; margin: 0; color: #5e6f86; font-size: 11px; line-height: 1.45; cursor: pointer; }
.wide-consent input { margin-top: 2px; }
.quote-placeholder { text-align: center; color: #76869c; padding: 80px 8px 20px; }
.quote-placeholder i { font-size: 40px; color: #9bafc7; }
.quote-placeholder h3 { color: #40536e; margin: 12px 0 7px; }
.quote-placeholder p { font-size: 13px; line-height: 1.5; }
.simulator-alert { max-width: 820px; margin: 0 auto 18px; display: flex; align-items: center; gap: 8px; border-radius: 10px; }
.result-sheet { max-width: 920px; margin: auto; overflow: hidden; border: 1px solid #e2e8f0; border-radius: 8px 30px 8px 30px; background: #fff; box-shadow: 0 24px 65px rgba(24, 50, 87, .12); }
.result-hero { position: relative; overflow: hidden; padding: 42px 48px 34px; background: #153a69; color: #fff; text-align: center; }
.result-hero::before, .result-hero::after { content: ""; position: absolute; width: 170px; height: 170px; border: 1px solid rgba(216,174,76,.38); transform: rotate(45deg); }
.result-hero::before { top: -115px; left: -50px; }
.result-hero::after { right: -65px; bottom: -125px; }
.result-finish { position: relative; z-index: 1; display: inline-flex; align-items: center; gap: 5px; padding: 6px 11px; margin-bottom: 20px; border-radius: 999px; background: rgba(255,255,255,.1); color: #f1d98e; font-size: 10px; font-weight: 800; letter-spacing: .8px; text-transform: uppercase; }
.result-kicker { position: relative; z-index: 1; display: block; color: #a9c6e5; font-size: 10px; font-weight: 800; letter-spacing: 1.4px; text-transform: uppercase; }
.result-hero h1 { position: relative; z-index: 1; margin: 6px 0 8px; color: #fff; font-size: 36px; }
.result-hero > p { position: relative; z-index: 1; max-width: 610px; margin: 0 auto 24px; color: #d2dfed; line-height: 1.55; }
.result-score { position: relative; z-index: 1; display: inline-grid; grid-template-columns: auto auto; align-items: end; column-gap: 7px; padding: 15px 24px; border-radius: 5px 17px 5px 17px; background: #fff; color: #153a69; text-align: left; }
.result-score small { grid-column: span 2; color: #708098; font-size: 9px; text-transform: uppercase; letter-spacing: .6px; }
.result-score strong { font-size: 35px; line-height: 1; }
.result-score span { color: #a07823; font-size: 12px; font-weight: 800; }
.mockup-notice { position: relative; z-index: 1; display: flex; align-items: flex-start; gap: 8px; max-width: 680px; margin: 20px auto 0; padding: 10px 13px; border-radius: 4px 12px 4px 12px; background: rgba(255,255,255,.08); color: #cfdeed; text-align: left; font-size: 10px; line-height: 1.45; }
.mockup-notice i { color: #e1bf64; font-size: 16px; }
.result-program { padding: 25px 40px; border-bottom: 1px solid #e7ecf2; text-align: center; background: #f7f9fc; }
.result-program span { color: #9a7526; font-size: 9px; font-weight: 900; letter-spacing: 1px; text-transform: uppercase; }
.result-program h2 { margin: 5px 0 0; color: #17345f; font-size: 24px; }
.result-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; padding: 32px 36px; }
.result-panel { overflow: hidden; border: 1px solid #e4e9f0; border-radius: 5px 18px 5px 18px; background: #fff; }
.result-panel-title, .values-heading { display: flex; align-items: center; gap: 10px; padding: 15px 18px; background: #f5f7fa; }
.result-panel-title > span, .values-heading > span { flex: 0 0 29px; height: 26px; display: grid; place-items: center; border-radius: 4px 10px 4px 10px; background: #d8ae4c; color: #173047; font-size: 9px; font-weight: 900; }
.result-panel-title h3, .values-heading h3 { margin: 0; color: #17345f; font-size: 14px; }
.result-panel-title p, .values-heading p { margin: 2px 0 0; color: #8490a1; font-size: 9px; }
.result-list { margin: 0; padding: 12px 18px 16px; }
.result-list > div { display: grid; grid-template-columns: minmax(0, 1fr) minmax(100px, auto); gap: 14px; padding: 8px 0; border-bottom: 1px dashed #e7ebf0; }
.result-list > div:last-child { border-bottom: 0; }
.result-list dt { color: #66758a; font-size: 10px; font-weight: 600; }
.result-list dd { margin: 0; color: #203653; font-size: 10px; font-weight: 800; text-align: right; overflow-wrap: anywhere; }
.score-list dd { color: #a07823; }
.result-values { margin: 0 36px 34px; overflow: hidden; border: 1px solid #d8ae4c; border-radius: 6px 22px 6px 22px; }
.values-heading { padding: 18px 22px; border-bottom: 1px solid #e8edf2; background: #fffaf0; }
.values-columns { display: grid; grid-template-columns: 1fr 1fr; }
.value-block { padding: 22px; }
.value-block + .value-block { border-left: 1px solid #e5eaf0; background: #f7f9fc; }
.value-block > span { display: block; margin-bottom: 12px; color: #a07823; font-size: 9px; font-weight: 900; letter-spacing: .8px; text-transform: uppercase; }
.value-block > div { display: flex; justify-content: space-between; gap: 12px; padding: 9px 0; }
.value-block small { color: #637288; font-size: 10px; }
.value-block strong { color: #17345f; font-size: 12px; text-align: right; }
.value-block .annual-total { margin-top: 7px; padding-top: 14px; border-top: 2px solid #d8ae4c; }
.value-block .annual-total strong { font-size: 16px; }
.value-block strong.pending-value { color: #a07823; font-size: 11px; }
.values-disclaimer { margin: 0; padding: 13px 22px; border-top: 1px solid #e4e9ef; background: #f1f4f7; color: #758297; font-size: 9px; line-height: 1.5; text-align: center; }
.result-actions { padding: 0 36px 36px; text-align: center; }
.result-actions .site-link { margin-top: 16px; }
.success-card { text-align: center; max-width: 680px; padding: 48px; }
.success-icon { width: 72px; height: 72px; display: grid; place-items: center; margin: 0 auto 18px; border-radius: 50%; background: #e5f7ee; color: #209461; font-size: 42px; }
.success-card p { color: #6f7d91; font-size: 15px; line-height: 1.6; max-width: 540px; margin: 0 auto 25px; }
.success-quote { border-radius: 15px; padding: 24px; background: linear-gradient(140deg, #14345e, #1c568c); color: #fff; margin: 0 auto 25px; display: flex; flex-direction: column; }
.success-quote span { font-weight: 700; font-size: 17px; }
.success-quote small { color: #b9d1e8; margin-top: 15px; }
.success-quote strong { font-size: 34px; }
.success-quote div { display: flex; justify-content: center; gap: 12px; color: #c4d4e5; }
.success-quote b { color: #ead58d; }
.score-disclaimer { margin: -2px auto 12px; max-width: 430px; }
.result-enrollment { margin-top: 8px; color: #d6e2ef; }
.narrow-card { max-width: 640px; }
.journey-card { position: relative; overflow: hidden; border: 0; border-radius: 6px 24px 24px 6px; box-shadow: 0 20px 55px rgba(20, 46, 88, .1); }
.journey-card::before { content: ""; position: absolute; inset: 0 auto 0 0; width: 7px; background: linear-gradient(180deg, #d8ae4c, #173f70 68%); }
.journey-heading { display: grid; grid-template-columns: 58px 1fr; gap: 18px; align-items: center; padding: 4px 0 27px; margin-bottom: 28px; border-bottom: 1px solid #e8edf3; }
.journey-symbol { width: 58px; height: 58px; display: grid; place-items: center; border-radius: 18px 5px 18px 5px; background: #173f70; color: #fff; font-size: 28px; box-shadow: 7px 7px 0 #f0dfb4; }
.journey-kicker { display: block; margin-bottom: 5px; color: #a07823; font-size: 10px; font-weight: 900; letter-spacing: 1.2px; text-transform: uppercase; }
.journey-heading h2 { margin: 0 0 5px; color: #142f56; font-size: 24px; letter-spacing: -.25px; }
.journey-heading p { margin: 0; color: #758298; line-height: 1.5; }
.pregrado-single { max-width: 1040px; }
.section-progress { display: grid; grid-template-columns: auto 1fr; gap: 18px; align-items: center; padding: 14px 18px; margin-bottom: 8px; border-radius: 5px 15px 5px 15px; background: #f5f7fa; }
.section-progress > div:first-child { display: flex; align-items: baseline; gap: 6px; white-space: nowrap; }
.section-progress strong { color: #173f70; font-size: 17px; }
.section-progress span { color: #748399; font-size: 11px; }
.section-progress-track { height: 7px; overflow: hidden; border-radius: 999px; background: #dfe5ec; }
.section-progress-track span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #d8ae4c, #173f70); transition: width .3s ease; }
.single-section { padding: 26px 0; border-bottom: 1px solid #e8edf3; }
.single-section:first-of-type { padding-top: 0; }
.single-section-title { display: flex; gap: 13px; align-items: flex-start; margin-bottom: 20px; }
.single-section-title > span { flex: 0 0 36px; height: 32px; display: grid; place-items: center; border-radius: 5px 12px 5px 12px; background: #173f70; color: #fff; font-size: 11px; font-weight: 900; box-shadow: 4px 4px 0 #f0dfb4; }
.single-section-title h3 { margin: 0 0 3px; color: #17345f; font-size: 18px; }
.single-section-title p { margin: 0; color: #7b8799; font-size: 12px; }
.section-state { display: inline-flex; align-items: center; gap: 3px; margin-left: auto; padding: 5px 9px; border-radius: 999px; background: #e3f4eb; color: #247650; font-size: 10px; font-weight: 800; }
.section-fields { min-width: 0; margin: 0; padding: 0; border: 0; }
.section-locked .single-section-title { margin-bottom: 12px; opacity: .62; }
.section-locked .single-section-title > span { background: #aeb8c5; box-shadow: 4px 4px 0 #e4e8ed; }
.section-locked .section-fields { opacity: .42; filter: grayscale(.45); }
.section-lock-note { display: flex; align-items: center; gap: 7px; margin-bottom: 14px; padding: 10px 12px; border-radius: 5px 12px 5px 12px; background: #f1f3f6; color: #748196; font-size: 11px; font-weight: 700; }
.section-lock-note i { color: #9b7629; font-size: 15px; }
.section-complete > .single-section-title > span { background: #287656; box-shadow: 4px 4px 0 #d9eee4; }
.three-columns { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.selected-career-summary { grid-column: span 1; display: flex; flex-direction: column; justify-content: center; min-height: 78px; padding: 12px 15px; border-radius: 5px 15px 5px 15px; background: #edf4fc; border-left: 4px solid #d8ae4c; }
.selected-career-summary small { color: #76869b; font-size: 10px; text-transform: uppercase; letter-spacing: .6px; }
.selected-career-summary strong { color: #173f70; font-size: 13px; line-height: 1.35; }
.selected-career-summary span { color: #61758e; margin-top: 4px; font-size: 11px; }
.single-submit-panel { display: grid; grid-template-columns: minmax(220px, .7fr) minmax(280px, 1.3fr) auto; gap: 20px; align-items: center; margin-top: 28px; padding: 22px; border-radius: 6px 20px 6px 20px; background: #173f70; color: #fff; }
.single-quote { display: flex; flex-direction: column; }
.single-quote span { color: #bdd0e4; font-size: 10px; text-transform: uppercase; letter-spacing: .6px; }
.single-quote strong { font-size: 27px; line-height: 1.15; }
.single-quote small { color: #d9c583; font-size: 10px; }
.single-installments { display: flex; align-items: center; gap: 8px; margin-top: 10px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,.18); }
.single-installments select { width: 86px; height: 31px; border: 1px solid rgba(255,255,255,.24); border-radius: 5px; padding: 3px 6px; background: rgba(255,255,255,.12); color: #fff; font-size: 10px; }
.single-installments b { color: #fff; font-size: 13px; }
.single-installments b small { color: #adc1d7; font-weight: 500; }
.single-consent label { display: flex; gap: 9px; align-items: flex-start; margin: 0; color: #d8e3ef; font-size: 11px; line-height: 1.45; cursor: pointer; }
.single-consent input { margin-top: 2px; }
.single-submit-panel .primary-button { white-space: nowrap; background: #fff; color: #173f70; box-shadow: none; }
.single-submit-panel.submit-locked { background: #7d8a9b; }
.single-submit-panel.submit-locked .single-quote, .single-submit-panel.submit-locked .single-consent { opacity: .55; }
.single-submit-panel .primary-button:disabled { background: #dfe4ea; color: #687689; opacity: 1; }
.question-label { display: flex; align-items: center; gap: 11px; margin-bottom: 14px; }
.question-label > span { flex: 0 0 30px; width: 30px; height: 24px; display: grid; place-items: center; border-radius: 4px 10px 4px 10px; background: #f2e5c3; color: #7d5d19; font-size: 10px; font-weight: 900; }
.question-label h3 { margin: 0; color: #213653; font-size: 16px; }
.question-label-spaced { margin-top: 32px; }
.course-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 9px; margin-bottom: 4px; }
.course-button, .choice-button { position: relative; border: 1px solid #d9e0e9; background: #f9fafc; color: #364860; transition: border-color .18s, background .18s, transform .18s; }
.course-button { min-height: 72px; border-radius: 8px 17px 8px 17px; padding: 27px 8px 10px; font-weight: 700; }
.course-button:hover, .choice-button:hover { border-color: #8098b3; background: #fff; transform: translateY(-1px); }
.course-button.selected, .choice-button.selected { background: #173f70; color: #fff; border-color: #173f70; box-shadow: 0 7px 16px rgba(23, 63, 112, .16); }
.select-dot { position: absolute; top: 8px; right: 8px; width: 17px; height: 17px; display: grid; place-items: center; border: 1px solid #bdc8d5; border-radius: 50%; color: transparent; font-size: 12px; }
.selected .select-dot { border-color: #d8ae4c; background: #d8ae4c; color: #173047; }
.school-fields { padding: 21px; border-radius: 5px 18px 18px 18px; background: #f6f8fb; }
.field-message { color: #d14949; font-size: 11px; margin-top: 4px; }
.academic-question, .benefit-question { padding: 20px; margin-bottom: 14px; border: 1px solid #e4e9f0; border-radius: 5px 18px 18px 18px; background: #fff; }
.academic-question { display: grid; grid-template-columns: 1fr minmax(170px, .75fr); gap: 20px; align-items: start; }
.academic-question .question-label { margin: 11px 0 0; align-items: flex-start; }
.academic-input { margin: 0; }
.academic-input .form-control { height: 54px; background: #f7f9fc; border-width: 0 0 2px; border-radius: 4px; font-size: 20px; font-weight: 800; color: #173f70; }
.academic-input small { display: block; color: #738095; margin-top: 7px; line-height: 1.5; }
.elective-options { display: flex; gap: 24px; padding: 12px 0; }
.elective-options label { font-weight: 500; }
.elective-options input { margin-right: 6px; }
.final-pregrado-quote { min-height: 0; }
.choice-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 9px; }
.choice-grid.three { grid-template-columns: repeat(3, 1fr); }
.choice-button { min-height: 52px; border-radius: 5px 13px 5px 13px; padding: 11px 28px 11px 10px; }
.academic-summary { border-radius: 16px; background: #eef5fc; color: #60728a; padding: 38px 25px; text-align: center; align-self: start; }
.academic-summary i { color: #24639d; font-size: 38px; }
.academic-summary h3 { color: #23456e; margin: 10px 0 7px; }
.site-link { display: block; margin-top: 18px; color: #416b98; }
.public-footer { min-height: 70px; padding: 20px max(20px, calc((100% - 1120px) / 2)); background: #112a4d; color: #9fb1c8; display: flex; justify-content: space-between; align-items: center; gap: 20px; font-size: 11px; }
.public-footer span:first-child { color: #fff; font-weight: 700; }
@media (max-width: 800px) {
    .simulator-content { padding-top: 30px; }
    .intro h1, .success-card h1 { font-size: 29px; }
    .form-card { padding: 25px 20px; }
    .type-grid, .program-layout { grid-template-columns: 1fr; }
    .wide-contact-grid { grid-template-columns: 1fr 1fr; }
    .wide-contact-footer { grid-template-columns: 1fr; }
    .type-card { min-height: 190px; }
    .quote-card { min-height: 0; }
    .form-grid { grid-template-columns: 1fr; }
    .three-columns { grid-template-columns: 1fr 1fr; }
    .single-submit-panel { grid-template-columns: 1fr; }
    .section-progress { grid-template-columns: 1fr; gap: 8px; }
    .choice-grid, .choice-grid.three { grid-template-columns: 1fr 1fr; }
    .course-grid { grid-template-columns: 1fr 1fr; }
    .academic-question { grid-template-columns: 1fr; gap: 14px; }
    .journey-card { border-radius: 5px 18px 18px 5px; }
    .journey-heading { grid-template-columns: 48px 1fr; }
    .journey-symbol { width: 48px; height: 48px; font-size: 23px; }
    .span-2 { grid-column: span 1; }
    .three-columns .span-2 { grid-column: span 2; }
    .progress-step { min-width: 76px; }
    .progress-step small { font-size: 10px; }
    .progress-line { width: 35px; }
    .public-footer { flex-direction: column; text-align: center; }
    .result-grid, .values-columns { grid-template-columns: 1fr; }
    .value-block + .value-block { border-top: 1px solid #e5eaf0; border-left: 0; }
    .result-hero { padding: 35px 24px 28px; }
    .result-grid { padding: 24px 20px; }
    .result-values { margin: 0 20px 28px; }
    .result-actions { padding: 0 20px 28px; }
}
@media (max-width: 520px) {
    .header-help { display: none; }
    .header-inner { justify-content: center; }
    .intro p { font-size: 15px; }
    .progress-step small { display: none; }
    .success-card { padding: 35px 20px; }
    .three-columns { grid-template-columns: 1fr; }
    .three-columns .span-2, .selected-career-summary { grid-column: span 1; }
    .single-section { padding: 22px 0; }
    .single-submit-panel { padding: 18px; }
    .wide-contact-grid { grid-template-columns: 1fr; }
    .contact-request-wide { padding: 21px 17px; }
}
</style>
